import React, { useState } from 'react';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Table, Column } from '../../components/ui/Table';
import { Search, Eye, Download } from 'lucide-react';
interface AuditLog {
  id: string;
  loanAppId: string;
  merchantId: string;
  productId: string;
  creationDate: string;
  event: string;
  status: string;
  actor: string;
  details: string;
}
export function LoanApplicationAuditLogs() {
  const [searchQuery, setSearchQuery] = useState('');
  // Mock Data
  const auditLogs: AuditLog[] = [{
    id: '1',
    loanAppId: 'LA-2024-001',
    merchantId: 'M-12345',
    productId: 'P-001',
    creationDate: '2024-01-15 10:30:00',
    event: 'Application Created',
    status: 'Draft',
    actor: 'Customer Portal',
    details: 'Initial application submission'
  }, {
    id: '2',
    loanAppId: 'LA-2024-001',
    merchantId: 'M-12345',
    productId: 'P-001',
    creationDate: '2024-01-15 10:32:15',
    event: 'OTP Generated',
    status: 'Pending Verification',
    actor: 'System',
    details: 'OTP sent to +92-300-1234567'
  }, {
    id: '3',
    loanAppId: 'LA-2024-001',
    merchantId: 'M-12345',
    productId: 'P-001',
    creationDate: '2024-01-15 10:33:45',
    event: 'OTP Verified',
    status: 'Verified',
    actor: 'Customer',
    details: 'Successful verification'
  }, {
    id: '4',
    loanAppId: 'LA-2024-001',
    merchantId: 'M-12345',
    productId: 'P-001',
    creationDate: '2024-01-15 10:35:00',
    event: 'KYC Check Completed',
    status: 'KYC Passed',
    actor: 'System',
    details: 'NADRA verification successful'
  }, {
    id: '5',
    loanAppId: 'LA-2024-001',
    merchantId: 'M-12345',
    productId: 'P-001',
    creationDate: '2024-01-15 10:36:30',
    event: 'Credit Score Evaluated',
    status: 'Score: 720',
    actor: 'Credit Bureau',
    details: 'eCIB inquiry completed'
  }, {
    id: '6',
    loanAppId: 'LA-2024-001',
    merchantId: 'M-12345',
    productId: 'P-001',
    creationDate: '2024-01-15 10:38:00',
    event: 'Status Changed',
    status: 'Active → Under Review',
    actor: 'admin@credsys.com',
    details: 'Maker initiated review'
  }, {
    id: '7',
    loanAppId: 'LA-2024-001',
    merchantId: 'M-12345',
    productId: 'P-001',
    creationDate: '2024-01-15 11:00:00',
    event: 'Maker-Checker Decision',
    status: 'Approved',
    actor: 'checker@credsys.com',
    details: 'Approved with remarks: Standard approval'
  }, {
    id: '8',
    loanAppId: 'LA-2024-001',
    merchantId: 'M-12345',
    productId: 'P-001',
    creationDate: '2024-01-15 11:05:00',
    event: 'Cooling Off Period Started',
    status: 'Cooling Off',
    actor: 'System',
    details: 'Period: 24 hours'
  }, {
    id: '9',
    loanAppId: 'LA-2024-002',
    merchantId: 'M-67890',
    productId: 'P-002',
    creationDate: '2024-01-16 09:15:00',
    event: 'Application Withdrawn',
    status: 'Withdrawn',
    actor: 'Customer',
    details: 'Reason: Changed mind'
  }, {
    id: '10',
    loanAppId: 'LA-2024-003',
    merchantId: 'M-11111',
    productId: 'P-001',
    creationDate: '2024-01-16 14:30:00',
    event: 'Disbursement Ready',
    status: 'Ready for Disbursement',
    actor: 'ops@credsys.com',
    details: 'All checks completed'
  }];
  const filteredLogs = auditLogs.filter(log => log.loanAppId.toLowerCase().includes(searchQuery.toLowerCase()) || log.merchantId.toLowerCase().includes(searchQuery.toLowerCase()) || log.event.toLowerCase().includes(searchQuery.toLowerCase()) || log.actor.toLowerCase().includes(searchQuery.toLowerCase()));
  const columns: Column<AuditLog>[] = [{
    key: 'loanAppId',
    label: 'Loan App ID',
    render: item => <span className="font-medium text-blue-600" data-id="element-1947">{item.loanAppId}</span>
  }, {
    key: 'merchantId',
    label: 'Merchant ID'
  }, {
    key: 'productId',
    label: 'Product ID'
  }, {
    key: 'creationDate',
    label: 'Date/Time',
    render: item => <span className="text-sm text-gray-600" data-id="element-1948">{item.creationDate}</span>
  }, {
    key: 'event',
    label: 'Event',
    render: item => <span className="font-medium text-gray-900" data-id="element-1949">{item.event}</span>
  }, {
    key: 'status',
    label: 'Status',
    render: item => <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1950">
          {item.status}
        </span>
  }, {
    key: 'actor',
    label: 'Actor',
    render: item => <span className="text-sm text-gray-700" data-id="element-1951">{item.actor}</span>
  }, {
    key: 'details',
    label: 'Details',
    render: item => <span className="text-sm text-gray-600" data-id="element-1952">{item.details}</span>
  }];
  return <div className="space-y-8 animate-in fade-in duration-500" data-id="element-1953">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1954">
        <div data-id="element-1955">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight" data-id="element-1956">
            Loan Application Audit Logs
          </h1>
          <p className="text-gray-500 mt-1" data-id="element-1957">
            Comprehensive tracking of all loan application events and changes
          </p>
        </div>
        <Button size="lg" className="shadow-sm" data-id="element-1958">
          <Download className="mr-2 h-5 w-5" data-id="element-1959" />
          Export Logs
        </Button>
      </div>

      {/* Search */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-1960">
        <div className="relative" data-id="element-1961">
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" data-id="element-1962" />
          <Input placeholder="Search by Loan App ID, Merchant ID, Event, or Actor..." className="pl-10 border-0 bg-gray-50 focus:bg-white transition-colors" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1963" />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1964">
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-1965">
          <div className="text-sm text-gray-500" data-id="element-1966">Total Events</div>
          <div className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1967">1,247</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-1968">
          <div className="text-sm text-gray-500" data-id="element-1969">Applications Tracked</div>
          <div className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1970">342</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-1971">
          <div className="text-sm text-gray-500" data-id="element-1972">Status Changes</div>
          <div className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1973">856</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-1974">
          <div className="text-sm text-gray-500" data-id="element-1975">Today's Events</div>
          <div className="text-2xl font-bold text-blue-600 mt-1" data-id="element-1976">47</div>
        </div>
      </div>

      {/* Table */}
      <Table columns={columns} data={filteredLogs} onRowClick={log => console.log('View details:', log)} keyExtractor={item => item.id} data-id="element-1977" />
    </div>;
}